import React, { useState, useEffect, useRef } from 'react'
import config from '../../config.js'
import { groupColor } from './theme'
import { formatSeconds, formatInches, formatWeight, formatCurrency, formatPct } from './formatters'

const NESTER = config.NESTER_API_URL

/**
 * Per-group detail view. All the fidelity of the legacy NestResultsPanel,
 * scoped to ONE group: full layout SVG (interactive), metric strip, time +
 * cost breakdowns side-by-side on wide screens, parts summary table.
 */
export default function NesterGroupDetail({
  colors,
  mode,
  group,
  mg,
  idx,
  highlightedPartId,
  onHighlightPart,
  onExport,
}) {
  const accent = groupColor(idx)
  const de = group.detailed_estimate
  const cs = group.cost_summary
  const [wideEnough, setWideEnough] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1180)

  useEffect(() => {
    function onResize() { setWideEnough(window.innerWidth >= 1180) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const title = mg?.group_name
    || group.material_group_name
    || [mg?.material_type, mg?.thickness_display].filter(Boolean).join(' ')
    || `Group ${idx + 1}`

  const spec = [
    mg?.material_type,
    mg?.thickness_display,
    mg?.surface_finish,
    mg?.sheet_width && mg?.sheet_length ? `${mg.sheet_width}×${mg.sheet_length}"` : null,
    mg?.gas_type,
    mg?.feedrate_ipm ? `${mg.feedrate_ipm} IPM` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div style={{ padding: 16 }}>
      {/* Sticky summary strip */}
      <div style={styles.summaryStrip(colors, accent)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: accent }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>{title}</div>
            {spec && <div style={{ fontSize: 11, color: colors.textMuted }}>{spec}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, marginLeft: 'auto', alignItems: 'center' }}>
          <Chip colors={colors} label="Sheets" value={group.sheets_needed} />
          <Chip colors={colors} label="Util" value={formatPct(group.utilization_pct)} />
          <Chip colors={colors} label="Time" value={formatSeconds(de?.total_sec || group.est_total_time_sec)} />
          <Chip colors={colors} label="Pierces" value={group.total_pierce_count} />
          <Chip colors={colors} label="Total" value={formatCurrency(cs?.total_cost)} emphasis />
          {cs?.cost_per_part != null && <Chip colors={colors} label="$/Part" value={formatCurrency(cs.cost_per_part)} />}
          {onExport && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => onExport('csv', group.nest_id)} style={styles.exportBtn(colors)}>CSV</button>
              <button onClick={() => onExport('pdf', group.nest_id)} style={styles.exportBtn(colors)}>PDF</button>
            </div>
          )}
        </div>
      </div>

      {/* Interactive nest layout */}
      {group.nest_id && (
        <InteractiveNestSvg
          colors={colors}
          nestId={group.nest_id}
          sheetGroups={group.sheet_groups}
          sheetsNeeded={group.sheets_needed}
          highlightedPartId={highlightedPartId}
          onHighlightPart={onHighlightPart}
        />
      )}

      {/* Two-column breakdowns on wide screens */}
      <div style={{
        display: wideEnough ? 'grid' : 'block',
        gridTemplateColumns: wideEnough ? '1fr 1fr' : undefined,
        gap: 16,
        marginTop: 16,
      }}>
        {de && <TimeBreakdownTable colors={colors} de={de} />}
        {cs && <CostBreakdownTable colors={colors} cs={cs} nestResult={group} />}
      </div>

      {/* Time bar spanning full width */}
      {de && <TimeBar colors={colors} de={de} />}

      {/* Parts summary */}
      {group.parts_summary && group.parts_summary.length > 0 && (
        <PartsSummaryTable
          colors={colors}
          partsSummary={group.parts_summary}
          highlightedPartId={highlightedPartId}
          onHighlightPart={onHighlightPart}
        />
      )}
    </div>
  )
}

/* ── Interactive nest SVG (click-to-highlight) ──────────────────────── */

function InteractiveNestSvg({ colors, nestId, sheetGroups, sheetsNeeded, highlightedPartId, onHighlightPart }) {
  const [svg, setSvg] = useState(null)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!nestId) { setSvg(''); return }
    let cancelled = false
    setSvg(null)
    fetch(`${NESTER}/nests/${nestId}/svg`)
      .then(r => r.ok ? r.text() : '')
      .then(text => { if (!cancelled) setSvg(text || '') })
      .catch(() => { if (!cancelled) setSvg('') })
    return () => { cancelled = true }
  }, [nestId])

  // After the SVG lands in the DOM, wire click handlers on any node that
  // carries a data-part-id or a class like "part-N" / id="part-N".
  useEffect(() => {
    if (!wrapRef.current || !svg) return
    const root = wrapRef.current
    const handlers = []
    const nodes = root.querySelectorAll('[data-part-id], [class*="part-"], [id^="part-"]')
    nodes.forEach(node => {
      const partId = extractPartId(node)
      if (!partId) return
      node.style.cursor = 'pointer'
      const onClick = (e) => {
        e.stopPropagation()
        onHighlightPart?.(partId)
      }
      node.addEventListener('click', onClick)
      handlers.push([node, onClick])
    })
    return () => {
      handlers.forEach(([node, fn]) => node.removeEventListener('click', fn))
    }
  }, [svg, onHighlightPart])

  // Apply visual highlight to matching nodes when highlightedPartId changes
  useEffect(() => {
    if (!wrapRef.current) return
    const root = wrapRef.current
    const all = root.querySelectorAll('[data-part-id], [class*="part-"], [id^="part-"]')
    all.forEach(node => {
      const partId = extractPartId(node)
      if (partId && partId === String(highlightedPartId)) {
        node.setAttribute('data-highlighted', 'true')
        node.style.outline = '2px solid #facc15'
        node.style.filter = 'drop-shadow(0 0 4px #facc15)'
      } else {
        node.removeAttribute('data-highlighted')
        node.style.outline = ''
        node.style.filter = ''
      }
    })
  }, [highlightedPartId, svg])

  const hasDedup = sheetGroups && sheetGroups.length > 0 && sheetGroups.some(g => g.count > 1)

  return (
    <div>
      {hasDedup && (
        <div style={styles.sheetGroupBar(colors)}>
          {sheetGroups.map((g, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: colors.textFaint }}> · </span>}
              Sheet {g.template_sheet}{g.count > 1 ? ` ×${g.count}` : ''}
            </span>
          ))}
          <span style={{ marginLeft: 8, color: colors.textFaint }}>
            ({sheetsNeeded} total, {sheetGroups.length} unique layout{sheetGroups.length > 1 ? 's' : ''})
          </span>
        </div>
      )}
      <div style={styles.svgWrap(colors)}>
        {svg === null && <div style={{ padding: 20, color: colors.textMuted }}>Loading layout…</div>}
        {svg === '' && <div style={{ padding: 20, color: colors.warn }}>No layout available.</div>}
        {svg && <div ref={wrapRef} dangerouslySetInnerHTML={{ __html: svg }} />}
      </div>
      {svg && onHighlightPart && (
        <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 4, textAlign: 'center', fontStyle: 'italic' }}>
          Tip: click a part on the nest to highlight it in the parts table below.
        </div>
      )}
    </div>
  )
}

function extractPartId(node) {
  const data = node.getAttribute?.('data-part-id')
  if (data) return String(data)
  const id = node.getAttribute?.('id') || ''
  const m1 = id.match(/^part-(\d+)/)
  if (m1) return m1[1]
  const cls = node.getAttribute?.('class') || ''
  const m2 = cls.match(/part-(\d+)/)
  if (m2) return m2[1]
  return null
}

/* ── Time + cost tables ────────────────────────────────────────────── */

function TimeBreakdownTable({ colors, de }) {
  const rows = [
    { label: 'Cut Path', time: de.total_cut_path_sec, length: de.total_path_length_in,
      tip: 'Beam-on time cutting along part contours (exterior + interior).' },
    { label: 'SH Cut', time: de.total_sh_cut_sec, length: de.total_sh_length_in,
      tip: 'Skeleton cutting — separating finished parts from scrap material.' },
    { label: 'Move', time: de.total_move_sec, length: de.total_move_length_in,
      tip: 'Rapid traverse between pierce points, laser off.' },
    { label: 'Pierce', time: de.total_pierce_sec, length: null,
      tip: 'Dwell time at each pierce point to burn through.' },
    (de.total_nozzle_clean_sec > 0) && { label: 'Nozzle Clean', time: de.total_nozzle_clean_sec, length: null,
      tip: 'Periodic nozzle cleaning every N pierces.' },
    { label: 'Alignment', time: de.total_alignment_sec, length: null,
      tip: 'Sheet alignment cycle, once per sheet.' },
    { label: 'Shuttle', time: de.total_reposition_sec, length: null,
      tip: 'Sheet swap/load time, once per sheet.' },
    { label: 'Other', time: de.total_other_sec, length: null,
      tip: 'Fixed overhead per sheet.' },
  ].filter(Boolean)

  const totalLength = (de.total_path_length_in || 0) + (de.total_sh_length_in || 0) + (de.total_move_length_in || 0)

  return (
    <div>
      <div style={styles.sectionTitle(colors)}>Time Breakdown — Total</div>
      <div style={styles.tableWrap}>
        <table style={styles.table(colors)}>
          <thead><tr style={styles.thead(colors)}>
            <th style={styles.th(colors)}>Component</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Time</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Length</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.label} title={r.tip} style={{ cursor: 'help' }}>
                <td style={styles.td(colors)}>
                  {r.label}
                  <span style={{ color: colors.textFaint, fontSize: 10, marginLeft: 4 }}>ⓘ</span>
                </td>
                <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatSeconds(r.time)}</td>
                <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {r.length != null ? formatInches(r.length, 0) : '—'}
                </td>
              </tr>
            ))}
            <tr style={styles.totalRow(colors)}>
              <td style={styles.td(colors)}>Total</td>
              <td style={{ ...styles.td(colors), textAlign: 'right', fontWeight: 700 }}>{formatSeconds(de.total_sec)}</td>
              <td style={{ ...styles.td(colors), textAlign: 'right', fontWeight: 700 }}>{formatInches(totalLength, 0)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TimeBar({ colors, de }) {
  const total = de.total_sec || 1
  const segments = [
    { key: 'cut_path', label: 'Cut', value: de.total_cut_path_sec, color: '#2563eb' },
    { key: 'sh_cut',   label: 'SH',  value: de.total_sh_cut_sec,   color: '#7c3aed' },
    { key: 'move',     label: 'Move', value: de.total_move_sec,    color: '#059669' },
    { key: 'pierce',   label: 'Pierce', value: de.total_pierce_sec, color: '#dc2626' },
    { key: 'nozzle',   label: 'Nozzle', value: de.total_nozzle_clean_sec || 0, color: '#f59e0b' },
    { key: 'align',    label: 'Align',  value: de.total_alignment_sec || 0,    color: '#8b5cf6' },
    { key: 'shuttle',  label: 'Shuttle', value: de.total_reposition_sec, color: '#d97706' },
    { key: 'other',    label: 'Other', value: de.total_other_sec, color: '#6b7280' },
  ].filter(s => s.value > 0)

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        display: 'flex', height: 22, borderRadius: 4, overflow: 'hidden',
        border: `1px solid ${colors.border}`,
      }}>
        {segments.map(s => (
          <div key={s.key}
            title={`${s.label}: ${formatSeconds(s.value)} (${((s.value / total) * 100).toFixed(1)}%)`}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color, minWidth: 2 }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6, fontSize: 11, color: colors.textMuted }}>
        {segments.map(s => (
          <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
            {s.label} {((s.value / total) * 100).toFixed(0)}%
          </span>
        ))}
      </div>
    </div>
  )
}

function CostBreakdownTable({ colors, cs, nestResult }) {
  const hasCosts = cs.material_cost != null || cs.gas_cost != null || cs.consumable_cost != null
  if (!hasCosts) return null

  const rows = [
    { label: 'Material', value: cs.material_cost, color: '#2563eb',
      detail: cs.raw_sheet_weight_lbs ? `${formatWeight(cs.raw_sheet_weight_lbs)} raw` : null },
    { label: 'Gas',      value: cs.gas_cost, color: '#d97706', detail: null },
    { label: 'Consumables', value: cs.consumable_cost, color: '#7c3aed', detail: null },
  ].filter(r => r.value != null)

  const total = cs.total_cost
  const totalParts = nestResult.parts_summary
    ? nestResult.parts_summary.reduce((s, p) => s + (p.quantity || 0), 0)
    : null

  return (
    <div>
      <div style={styles.sectionTitle(colors)}>Cost Breakdown</div>
      <div style={styles.tableWrap}>
        <table style={styles.table(colors)}>
          <thead><tr style={styles.thead(colors)}>
            <th style={styles.th(colors)}>Category</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Cost</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>% of Total</th>
            <th style={styles.th(colors)}>Detail</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.label}>
                <td style={styles.td(colors)}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.color, marginRight: 6 }} />
                  {r.label}
                </td>
                <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.value)}</td>
                <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {total ? ((r.value / total) * 100).toFixed(1) + '%' : '—'}
                </td>
                <td style={{ ...styles.td(colors), color: colors.textMuted, fontSize: 12 }}>{r.detail || '—'}</td>
              </tr>
            ))}
            <tr style={styles.totalRow(colors)}>
              <td style={styles.td(colors)}>Total</td>
              <td style={{ ...styles.td(colors), textAlign: 'right', fontWeight: 700 }}>{total != null ? formatCurrency(total) : '—'}</td>
              <td style={{ ...styles.td(colors), textAlign: 'right', fontWeight: 700 }}>100%</td>
              <td style={styles.td(colors)} />
            </tr>
            {cs.cost_per_part != null && (
              <tr style={{ background: colors.accentSoft }}>
                <td style={{ ...styles.td(colors), fontWeight: 700 }}>Cost / Part</td>
                <td style={{ ...styles.td(colors), textAlign: 'right', fontWeight: 700 }}>{formatCurrency(cs.cost_per_part)}</td>
                <td style={{ ...styles.td(colors), textAlign: 'right', fontSize: 12, color: colors.textMuted }}>
                  {totalParts ? `${totalParts} parts` : ''}
                </td>
                <td style={styles.td(colors)} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(cs.raw_sheet_weight_lbs != null || cs.scrap_pct != null) && (
        <div style={styles.weightBar(colors)}>
          {cs.raw_sheet_weight_lbs != null && <span>Raw: {formatWeight(cs.raw_sheet_weight_lbs)}</span>}
          {cs.total_parts_weight_lbs != null && <span>Parts: {formatWeight(cs.total_parts_weight_lbs)}</span>}
          {cs.scrap_weight_lbs != null && <span>Scrap: {formatWeight(cs.scrap_weight_lbs)}</span>}
          {cs.scrap_pct != null && (
            <span style={{ color: cs.scrap_pct > 50 ? colors.danger : colors.textMuted }}>
              ({formatPct(cs.scrap_pct)} scrap)
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function PartsSummaryTable({ colors, partsSummary, highlightedPartId, onHighlightPart }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.sectionTitle(colors)}>Parts Summary</div>
      <div style={styles.tableWrap}>
        <table style={styles.table(colors)}>
          <thead><tr style={styles.thead(colors)}>
            <th style={styles.th(colors)}>Part</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Size (in)</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Qty</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Cut Length</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Pierces</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Weight (g)</th>
            <th style={{ ...styles.th(colors), textAlign: 'right' }}>Process Time</th>
          </tr></thead>
          <tbody>
            {partsSummary.map(p => {
              const highlighted = String(p.part_id) === String(highlightedPartId)
              return (
                <tr key={p.part_id}
                  onClick={() => onHighlightPart?.(highlighted ? null : p.part_id)}
                  style={{
                    cursor: onHighlightPart ? 'pointer' : 'default',
                    background: highlighted ? colors.accentSoft : 'transparent',
                    transition: 'background 0.15s',
                  }}>
                  <td style={styles.td(colors)}>
                    {highlighted && <span style={{ color: '#facc15', marginRight: 4 }}>●</span>}
                    {p.part_label || p.file_name}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {p.bounding_width?.toFixed(2)} × {p.bounding_height?.toFixed(2)}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {p.quantity}
                    {p.fill_sheet && (
                      <span style={{ marginLeft: 4, fontSize: 10, color: colors.accent, fontWeight: 700 }}
                        title="Quantity auto-computed by Fill Sheet">(fill)</span>
                    )}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {p.cut_length_in?.toFixed(1)}"
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {p.pierce_count}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {p.weight_g?.toFixed(1)}
                  </td>
                  <td style={{ ...styles.td(colors), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatSeconds(p.est_process_time_sec)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ── Bits ─────────────────────────────────────────────────────────── */

function Chip({ colors, label, value, emphasis }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 56 }}>
      <span style={{ fontSize: 9, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</span>
      <span style={{
        fontSize: 13, fontWeight: 700,
        color: emphasis ? colors.accent : colors.text,
      }}>{value ?? '—'}</span>
    </div>
  )
}

const styles = {
  summaryStrip: (c, accent) => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px',
    background: c.panelAlt,
    border: `1px solid ${c.border}`,
    borderLeft: `4px solid ${accent}`,
    borderRadius: 6,
    marginBottom: 12,
    position: 'sticky', top: 50, zIndex: 8,
  }),
  exportBtn: (c) => ({
    padding: '3px 10px', fontSize: 11, fontWeight: 600,
    border: `1px solid ${c.border}`, background: c.panel, color: c.text,
    borderRadius: 4, cursor: 'pointer',
  }),
  sheetGroupBar: (c) => ({
    fontSize: 12, color: c.textMuted,
    marginBottom: 8,
    padding: '6px 10px',
    background: c.accentSoft,
    borderRadius: 4,
    border: `1px solid ${c.accentBorder}`,
  }),
  svgWrap: (c) => ({
    background: c.panelAlt, borderRadius: 6, padding: 10, textAlign: 'center',
    overflow: 'auto',
    border: `1px solid ${c.border}`,
  }),
  sectionTitle: (c) => ({
    fontSize: 13, fontWeight: 700, color: c.text,
    margin: '0 0 8px 0',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }),
  tableWrap: { overflowX: 'auto' },
  table: (c) => ({
    width: '100%', borderCollapse: 'collapse', fontSize: 13,
    background: c.panel, color: c.text,
  }),
  thead: (c) => ({
    background: c.panelAlt,
    fontSize: 11, fontWeight: 700, color: c.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }),
  th: (c) => ({
    padding: '6px 10px', textAlign: 'left',
    borderBottom: `1px solid ${c.border}`,
  }),
  td: (c) => ({
    padding: '6px 10px',
    borderBottom: `1px solid ${c.border}`,
  }),
  totalRow: (c) => ({
    background: c.panelAlt,
    fontWeight: 700,
  }),
  weightBar: (c) => ({
    display: 'flex', gap: 14, fontSize: 12, color: c.textMuted,
    marginTop: 8, padding: '6px 10px',
    background: c.panelAlt, borderRadius: 4, border: `1px solid ${c.border}`,
  }),
}
